<template>
    <div :id="componentId" class="dropdown"
         :ref="componentId"
         :style="{height: getHeight}"
         :class="{'dropdown-disabled': !options || isDisable}">
        <div @click.prevent="!isDisable && (toggleDisabled ? openMenu() : toggle())">
            <slot name="dropdownField" :dropdownFieldTitle="dropdownFieldTitle">
                <div class="dropdown-field font-5">
                    <div class="full-width truncate" :style="{'line-height': getHeight}">
                        <span> {{ dropdownFieldTitle }} </span>
                    </div>
                    <div v-if="!(isDisable)"
                         class="right-dropdown-arrow"
                         :class="{'right-dropdown-arrow-open': menuOpen}"></div>
                </div>
            </slot>
        </div>
        <div :id="`ssaCustomDropdownArea${uniqueId}`" v-if="menuOpen" class="dropdown-area font-5"
             v-iron-source-click-outside="{
                    handler: 'closeMenu',
                    exclude: [componentId]
                 }">
            <slot name="dropdownArea">
                <leavesDropdown :options="options"
                                 :selectedItem="selectedItem"
                                 :idField="idField"
                                 :displayField="displayField"
                                 @dropdownSelectedOption="onClick"
                                 @closeMenu="closeMenu"
                                 class="single-select">
                    <template #postOption="slotProps">
                        <slot name="postOption" :option="slotProps.option"/>
                    </template>
                    <template #preOption="slotProps">
                        <slot name="preOption" :option="slotProps.option"/>
                    </template>
                    <template #description="slotProps">
                        <slot name="description" :option="slotProps.option"/>
                    </template>
                </leavesDropdown>
            </slot>
            <div v-if="menuOpen">
                <slot name="dropdownFooter"/>
            </div>
        </div>
    </div>
</template>

<script>
import consts from 'services/metaData/consts';
import leavesDropdown from './LeavesDropdown.vue';
import ironSourceClickOutside from '../../directives/IronSourceClickOutside';

export default {
    name: 'BaseDropdown',
    components: {
        leavesDropdown
    },
    data() {
        return {
            menuOpen: false,
            selectedItem: null,
            selectedGenreValue: '',
            consts
        };
    },
    props: {
        uniqueId: String,
        defaultText: String,
        options: {
            type: Array,
            required: true,
            default: () => []
        },
        isDisable: Boolean,
        selectedId: {
            type: [Number, String]
        },
        displayField: {
            type: String,
            default: 'displayText'
        },
        idField: {
            type: String,
            default: 'id'
        },
        selectedDisplayTextField: {
            type: String,
            default: 'displayText'
        },
        height: {
            type: [Number, String],
            default: 32
        },
        maxListHeight: {
            type: [Number, String],
            default: 270
        },
        triggerCloseMenu: Boolean,
        customDisplayText: String,
        toggleDisabled: Boolean
    },
    computed: {
        componentId() {
            return `ssaCustomDropdownId${this.uniqueId}`;
        },
        dropdownFieldTitle() {
            return this.customDisplayText
                || (this.selectedItem || this.defaultItem || {})[this.selectedDisplayTextField];
        },
        defaultItem() {
            return { [this.idField]: '', [this.selectedDisplayTextField]: this.defaultText || '' };
        },
        getHeight() {
            return `${this.height - 2}px`;
        }
    },
    created() {
        this.selectedItem = this.searchSelectedItem();
    },
    watch: {
        isDisable() {
            this.menuOpen = false;
        },
        selectedId() {
            this.selectedItem = this.searchSelectedItem();
        },
        options() {
            this.selectedItem = this.searchSelectedItem();
        },
        triggerCloseMenu() {
            if (this.triggerCloseMenu) {
                this.closeMenu();
            }
        }
    },
    methods: {
        openMenu() {
            this.menuOpen = true;
            this.$emit('openMenu');
        },
        closeMenu() {
            this.menuOpen = false;
            this.filteredOptions = this.options;
            this.$emit('closeMenu');
        },
        toggle() {
            if (this.menuOpen) {
                this.closeMenu();
            } else {
                this.openMenu();
            }
        },
        onClick(selectedOption) {
            this.applySingleSelection(selectedOption);
            this.toggle();
        },
        applySingleSelection(selectedItem) {
            if (selectedItem === this.selectedItem) {
                return;
            }

            this.selectedItem = selectedItem;
            this.$emit('dropdownSelectedItemChanged', { ...this.selectedItem });
        },
        searchSelectedItem() {
            return this.options.find((x) => x[this.idField] === this.selectedId);
        }
    },
    directives: {
        ironSourceClickOutside
    }
};
</script>

<style scoped lang="scss">
@import "~styles/styleGuide";
@import "~styles/buttons";

$smallDropdownHeight: 25px;
$mediumDropdownHeight: 32px;
$largeDropdownHeight: 35px;

$dropdownHeightField: $mediumDropdownHeight;
$dropdownBorderBTWElements: $border;

$dropdownItemHeight: 40px;
$dropdownBorderBTWElements: $border;
$paddingLeftElements: 18px;

.dropdown {
    display: inline-block;
    height: $dropdownHeightField;
    position: relative;
}

.dropdown-field {
    min-width: 47px;
    line-height: calc(100% - 2px);
    padding-left: 12px;
    height: calc(100% - 2px);
    cursor: pointer;
    width: 100%;
    border-radius: $borderRadius;
    border: $border;


    @include inputGradient;

    .truncate {
        vertical-align: middle;
        line-height: 100%;
    }
    .right-dropdown-arrow { // the little arrow on the right
        text-align: center;
        display: block;
        position: absolute;
        width: 23px;
        height: inherit;
        top: 50%;
        right: 0;

        &:after {
            content: "";
            position: absolute;
            right: 50%;
            margin-right: -3px;
            margin-top: -2px;
            width: 0;
            height: 0;
            border-left: 4px solid transparent;
            border-right: 4px solid transparent;
            border-top: 4px solid $dark-300;
        }
    }
}

.dropdown-area {
    @include fadeIn;
    background-color: $light-100;
    color: $dark-300;
    @include applicationLayer;
    position: absolute;
    border-top: $border;
    box-shadow: 0 3px 10px 0 rgba(77,80,95,.3);
    border-radius: $borderRadius;
    min-width: 100%;
    max-width: 480px;
    width: auto;
    margin-top: 2px;
}

</style>
